import { z } from 'zod/v3'

import { m } from '@/lib/paraglide/messages'

/**
 * Tài khoản của một tích hợp SmartLab (máy vò chè hoặc camera hộ trồng).
 *
 * Factory chứ không phải hằng số: message validate là Paraglide `m.*()`, phải
 * gọi lúc dựng schema chứ không phải lúc load module.
 *
 * `hasPassword` = đã có mật khẩu lưu trong DB → để trống ô mật khẩu là giữ
 * nguyên. `requireHttps` bật cho camera: tài liệu tích hợp bắt buộc HTTPS vì
 * Basic Auth chỉ encode base64, đi HTTP là lộ mật khẩu.
 */
export function createIntegrationAccountSchema(options: {
    hasPassword: boolean
    requireHttps: boolean
}) {
    const { hasPassword, requireHttps } = options

    return z
        .object({
            enabled: z.boolean(),
            base_url: z.string().trim(),
            username: z.string().trim(),
            password: z.string(),
        })
        .superRefine((val, ctx) => {
            // Tắt tích hợp thì không soát gì: người dùng phải tắt được kể cả
            // khi các ô đang trống hoặc còn giá trị cũ chưa hợp lệ.
            if (!val.enabled) return

            if (!val.base_url) {
                ctx.addIssue({
                    path: ['base_url'],
                    code: z.ZodIssueCode.custom,
                    message: m.grower_camera_settings_base_url_required(),
                })
            } else if (!/^https?:\/\//i.test(val.base_url)) {
                ctx.addIssue({
                    path: ['base_url'],
                    code: z.ZodIssueCode.custom,
                    message: m.grower_camera_settings_base_url_invalid(),
                })
            } else if (requireHttps && !isSecureBaseUrl(val.base_url)) {
                ctx.addIssue({
                    path: ['base_url'],
                    code: z.ZodIssueCode.custom,
                    message: m.grower_camera_settings_base_url_https(),
                })
            }

            if (!val.username) {
                ctx.addIssue({
                    path: ['username'],
                    code: z.ZodIssueCode.custom,
                    message: m.grower_camera_settings_username_required(),
                })
            }
            if (!val.password && !hasPassword) {
                ctx.addIssue({
                    path: ['password'],
                    code: z.ZodIssueCode.custom,
                    message: m.grower_camera_settings_password_required(),
                })
            }
        })
}

export type IntegrationAccountFormValues = z.infer<
    ReturnType<typeof createIntegrationAccountSchema>
>

/** HTTPS, hoặc localhost khi dev với camera giả — khớp rule ở backend. */
function isSecureBaseUrl(value: string): boolean {
    const lower = value.toLowerCase()
    if (lower.startsWith('https://')) return true
    const host = lower.replace(/^https?:\/\//, '').split('/')[0]
    const hostname = host.split(':')[0]
    return hostname === 'localhost' || hostname === '127.0.0.1'
}

/** Khớp rule token ở backend (growercamera.ValidateToken). */
const CAMERA_TOKEN_PATTERN = /^[A-Za-z0-9_-]*$/
const CAMERA_TOKEN_MAX = 128

/**
 * Lấy token từ thứ người dùng dán vào: nguyên link camera được bàn giao, chỉ
 * phần đường dẫn, hay chính token.
 *
 * Cố ý KHÔNG biết khuôn đường dẫn của camera (khuôn đó chỉ nằm ở backend):
 * token luôn là đoạn cuối của link, nên chỉ cần bỏ query, lấy đoạn cuối và bỏ
 * đuôi tệp (`.mjpg`, `.m3u8`). Nhà cung cấp đổi khuôn thì hàm này vẫn đúng.
 */
export function extractCameraToken(raw: string): string {
    const value = raw.trim()
    if (!value) return ''
    const lastSegment = value.includes('/')
        ? (value.split(/[?#]/)[0].split('/').filter(Boolean).at(-1) ?? '')
        : value
    return lastSegment.replace(/\.[A-Za-z0-9]+$/, '')
}

/**
 * Camera của một vùng trồng. Chỉ nhập TOKEN — khuôn đường dẫn và base URL nằm
 * ở backend, nên người nhập không thể trỏ camera sang máy chủ khác và đổi
 * đường dẫn chỉ sửa một chỗ trong code.
 */
export function createAreaCameraSchema() {
    // transform + pipe (không dùng preprocess): giữ kiểu đầu vào là string để
    // react-hook-form suy đúng kiểu giá trị form.
    const tokenField = z
        .string()
        .transform(extractCameraToken)
        .pipe(
            z
                .string()
                .max(CAMERA_TOKEN_MAX, m.grower_area_camera_token_invalid())
                .regex(
                    CAMERA_TOKEN_PATTERN,
                    m.grower_area_camera_token_invalid(),
                ),
        )

    return z.object({
        name: z
            .string()
            .trim()
            .min(1, m.grower_area_camera_field_name_required()),
        stream_token: tokenField,
        control_token: tokenField,
    })
}

export type AreaCameraFormValues = z.infer<
    ReturnType<typeof createAreaCameraSchema>
>

/**
 * Nơi đặt camera: chọn hộ rồi chọn một vùng của hộ đó. Factory vì message là
 * Paraglide `m.*()`.
 */
export function createCameraLocationSchema() {
    return z.object({
        grower_id: z.string().min(1, m.camera_location_grower_required()),
        area_id: z.string().min(1, m.camera_location_area_required()),
    })
}

export type CameraLocationFormValues = z.infer<
    ReturnType<typeof createCameraLocationSchema>
>
